import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import useMediaQuery from '@mui/material/useMediaQuery';
import * as R from 'ramda';

import Header from './header/Header';
import MobileTabMenu from './tab-menu/MobileTabMenu';
import Overview from './overview-section/Overview';
import OverviewSkeleton from './overview-section/OverviewSkeleton';
import { useGetTerminalDetailsQuery } from './terminalDetailsEndpoints';

import TerminalDetailsAlerts from './alert-section/TerminalDetailsAlerts';
import { INITIAL_FILTERS_STATE } from './alert-section/constants';
import { useGetTerminalAlertsQuery } from '../terminal-locator/timsEndpoints';

const TerminalDetails = () => {
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'), { noSsr: true });
  const { terminalId } = useParams();

  const [selectedTab, setSelectedTab] = useState('alerts');
  const overviewRef = useRef();
  const alertRef = useRef();

  const handleTabClick = sectionRef => {
    sectionRef.current.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  useEffect(() => {
    const sections = [overviewRef, alertRef];

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (R.and(entry.isIntersecting, !R.equals(selectedTab, entry.target.id))) {
            setSelectedTab(entry.target.id);
          }
        });
      },
      {
        rootMargin: '-50% 0px -50% 0px',
        threshold: 0,
      }
    );

    sections.forEach(section => {
      observer.observe(section.current);
    });

    return () => {
      sections.forEach(ref => ref.current && observer.unobserve(ref.current));
    };
  }, [selectedTab]);

  const {
    data: terminalDetailsQuery = {},
    isFetching,
    isSuccess,
  } = useGetTerminalDetailsQuery({ terminalId: parseInt(terminalId) }, { skip: !terminalId });

  const terminalDetailsProps = R.prop(R.__, terminalDetailsQuery);

  const [filtersState, setFiltersState] = useState(INITIAL_FILTERS_STATE);

  const { data: terminalAlertsQuery = [] } = useGetTerminalAlertsQuery(
    { dateFilter: filtersState.timeRange.value, terminalId: parseInt(terminalId) },
    { skip: !terminalId }
  );

  const filteredAlerts = useMemo(() => {
    if (R.prop('outageCategory', filtersState).length) {
      return terminalAlertsQuery.filter(alert =>
        R.prop('outageCategory', filtersState).includes(alert.outageCategory)
      );
    }
    return terminalAlertsQuery;
  }, [filtersState, terminalAlertsQuery]);
  return (
    <Box sx={{ pt: 6 }}>
      <Header
        alertCount={terminalAlertsQuery.length}
        averageLoadTime={terminalDetailsProps('averageLoadTime')}
        currentWaitTime={terminalDetailsProps('currentWaitTime')}
        handleAlertClick={() => handleTabClick(alertRef)}
        handleOverviewClick={() => handleTabClick(overviewRef)}
        isFetching={isFetching}
        nextBestLoadTime={terminalDetailsProps('nextBestLoadTime')}
        selectedTab={selectedTab}
        setSelectedTab={setSelectedTab}
        terminalLat={terminalDetailsProps('latitude')}
        terminalLong={terminalDetailsProps('longitude')}
        terminalName={terminalDetailsProps('terminalIdName')}
      />
      <Box sx={{ backgroundColor: 'surface.white' }}>
        {isMobile && (
          <MobileTabMenu
            alertCount={terminalAlertsQuery.length}
            handleAlertClick={() => handleTabClick(alertRef)}
            handleOverviewClick={() => handleTabClick(overviewRef)}
            selectedTab={selectedTab}
          />
        )}
        <Box
          id="alerts"
          ref={alertRef}
          sx={{
            pb: 7,
            pt: 3,
            px: { md: 5, xs: 0 },
            scrollMarginTop: { lg: '276px', sm: '360px', xs: '276px' },
          }}
        >
          <TerminalDetailsAlerts
            alertCount={terminalAlertsQuery.length}
            filtersState={filtersState}
            isActiveEmail={terminalDetailsProps('isActiveEmail')}
            isActiveSMS={terminalDetailsProps('isActiveSMS')}
            setFiltersState={setFiltersState}
            terminalAlertsQuery={filteredAlerts}
          />
        </Box>
        <Box
          id="overview"
          ref={overviewRef}
          sx={{
            minHeight: '75vh',
            pb: 5,
            pt: { md: 3, xs: 0 },
            scrollMarginTop: { lg: '276px', sm: '360px', xs: '276px' },
          }}
        >
          {R.and(!isFetching, isSuccess) ? (
            <Overview terminalDetails={terminalDetailsQuery} />
          ) : (
            <OverviewSkeleton />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default TerminalDetails;
